/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import {
  Grid,
  CircularProgress,
  Box,
  Typography,
  CardHeader,
  Chip,
} from "@mui/material";
import { UserMonthlyActivity } from "../../api/dashboards";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import styled from "@emotion/styled";
import { Bar } from "react-chartjs-2";
import { alpha, useTheme } from "@mui/material/styles";
import "chart.js/auto";
import { NUMBER_FORMATTER } from "../utils/utils";

const ChartContainer = styled.div`
  height: 400px;
`;

const UserSelectContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

interface User {
  id: number;
  name: string;
}

interface IndividualUserTimelineProps {
  userActivityMonthly: UserMonthlyActivity[];
  isLoading: boolean;
  selectedUserId: number | "all";
  uniqueUsers: User[];
}

export const IndividualUserTimeline: React.FC<IndividualUserTimelineProps> = ({
  userActivityMonthly,
  isLoading,
  selectedUserId,
  uniqueUsers,
}) => {
  const theme = useTheme();

  const processChartData = () => {
    if (!userActivityMonthly || userActivityMonthly.length === 0) {
      return { labels: [], datasets: [] };
    }

    let filteredData = userActivityMonthly;
    if (selectedUserId !== "all") {
      filteredData = userActivityMonthly.filter(
        (item) => item.user_id === selectedUserId
      );
    }

    const sortedData = [...filteredData].sort(
      (a, b) =>
        new Date(a.activity_month).getTime() -
        new Date(b.activity_month).getTime()
    );

    if (selectedUserId !== "all") {
      const labels = sortedData.map((item) => {
        const date = new Date(item.activity_month);
        return date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
      });

      const monthlyChanges = sortedData.map((item) => item.monthly_changes);
      const monthlyApproved = sortedData.map((item) => item.monthly_approved);
      const monthlyRejected = sortedData.map((item) => item.monthly_rejected);
      const approvalRates = sortedData.map(
        (item) => item.monthly_approval_rate * 100
      );

      return {
        labels,
        datasets: [
          {
            label: "Approved Changes",
            backgroundColor: alpha(theme?.palette.success.main, 0.8),
            borderColor: theme?.palette.success.main,
            borderWidth: 1,
            data: monthlyApproved,
            yAxisID: "y",
          },
          {
            label: "Rejected Changes",
            backgroundColor: alpha(theme?.palette.error.main, 0.8),
            borderColor: theme?.palette.error.main,
            borderWidth: 1,
            data: monthlyRejected,
            yAxisID: "y",
          },
        ],
      };
    } else {
      const groupedByMonth = sortedData.reduce((acc, item) => {
        const monthKey = new Date(item.activity_month).toLocaleDateString(
          "en-US",
          {
            month: "short",
            year: "numeric",
          }
        );

        if (!acc[monthKey]) {
          acc[monthKey] = {
            totalChanges: 0,
            totalApproved: 0,
            totalRejected: 0,
            users: new Set(),
          };
        }

        acc[monthKey].totalChanges += item.monthly_changes;
        acc[monthKey].totalApproved += item.monthly_approved;
        acc[monthKey].totalRejected += item.monthly_rejected;
        acc[monthKey].users.add(item.user_name);

        return acc;
      }, {});

      const labels = Object.keys(groupedByMonth);
      const totalChanges = labels.map(
        (month) => groupedByMonth[month].totalChanges
      );
      const activeUsers = labels.map(
        (month) => groupedByMonth[month].users.size
      );

      const totalApproved = labels.map(
        (month) => groupedByMonth[month].totalApproved
      );
      const totalRejected = labels.map(
        (month) => groupedByMonth[month].totalRejected
      );

      return {
        labels,
        datasets: [
          {
            label: "Total Approved Changes",
            backgroundColor: alpha(theme?.palette.success.main, 0.8),
            borderColor: theme?.palette.success.main,
            borderWidth: 1,
            data: totalApproved,
            yAxisID: "y",
          },
          {
            label: "Total Rejected Changes",
            backgroundColor: alpha(theme?.palette.error.main, 0.8),
            borderColor: theme?.palette.error.main,
            borderWidth: 1,
            data: totalRejected,
            yAxisID: "y",
          },
        ],
      };
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text:
          selectedUserId !== "all"
            ? `Individual Performance Timeline - ${
                uniqueUsers.find((u) => u.id === selectedUserId)?.name || "User"
              }`
            : "Team Overview Timeline",
      },
    },
    scales: {
      x: {
        grid: {
          color: "rgba(0,0,0,0.0375)",
        },
      },
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        grid: {
          color: "rgba(0,0,0,0.0375)",
        },
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Changes",
        },
      },
    },
  };

  const chartData = processChartData();

  const getUserStats = () => {
    if (selectedUserId === "all") return null;

    const userActivities = userActivityMonthly.filter(
      (item) => item.user_id === selectedUserId
    );
    if (userActivities.length === 0) return null;

    const totalChanges = userActivities.reduce(
      (sum, item) => sum + item.monthly_changes,
      0
    );
    const totalApproved = userActivities.reduce(
      (sum, item) => sum + item.monthly_approved,
      0
    );
    const totalRejected = userActivities.reduce(
      (sum, item) => sum + item.monthly_rejected,
      0
    );
    const avgApprovalRate =
      totalChanges > 0 ? (totalApproved / totalChanges) * 100 : 0;

    return {
      totalChanges,
      totalApproved,
      totalRejected,
      avgApprovalRate,
      activeMonths: userActivities.length,
    };
  };

  const userStats = getUserStats();

  return (
    <>
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid>
          <Typography variant="h4" gutterBottom>
            Individual User Timeline
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={6}>
        <Grid
          size={{
            xs: 12,
            lg: 12,
          }}
        >
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <MiraCard>
              <CardHeader title="User Activity Over Time" />
              <MiraCardContent>
                {userStats && (
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    <Chip
                      size="small"
                      label={`${NUMBER_FORMATTER.format(
                        userStats.totalChanges
                      )} total changes`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`${Math.round(
                        userStats.avgApprovalRate
                      )}% avg approval`}
                      color={
                        userStats.avgApprovalRate > 80
                          ? "success"
                          : userStats.avgApprovalRate > 60
                          ? "warning"
                          : "error"
                      }
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`${userStats.activeMonths} active months`}
                      color="info"
                      variant="outlined"
                    />
                  </Box>
                )}

                <ChartContainer>
                  <Bar data={chartData} options={chartOptions} />
                </ChartContainer>
              </MiraCardContent>
            </MiraCard>
          )}
        </Grid>
      </Grid>
    </>
  );
};
